/**
 * Analytics Module
 *
 * Central export point for all analytics trackers.
 * Provides unified tracking function that dispatches events to all enabled platforms.
 *
 * @package WooAIChatbot
 * @since 0.2.0
 */
// Export individual trackers
export { ga4Tracker } from './ga4Tracker';
export { metaPixelTracker, } from './metaPixelTracker';
export { mixpanelTracker, } from './mixpanelTracker';
// Import trackers for unified tracking
import { ga4Tracker } from './ga4Tracker';
import { metaPixelTracker } from './metaPixelTracker';
import { mixpanelTracker } from './mixpanelTracker';
// Module-level configuration
let analyticsConfig = {};
let isInitialized = false;
/**
 * Initialize all analytics trackers
 *
 * @param config - Analytics configuration
 */
export function initAnalytics(config) {
    analyticsConfig = { ...config };
    // Initialize GA4 if configured
    if (config.ga4MeasurementId) {
        ga4Tracker.init(config.ga4MeasurementId, { debug: config.debug });
    }
    // Initialize Meta Pixel if configured
    if (config.metaPixelId) {
        metaPixelTracker.init(config.metaPixelId, { debug: config.debug });
    }
    // Initialize Mixpanel if configured
    if (config.mixpanelToken) {
        mixpanelTracker.init(config.mixpanelToken, { debug: config.debug });
    }
    isInitialized = true;
}
/**
 * Initialize trackers from WordPress globals
 *
 * Reads configuration from window.wooAiChatbot.analytics
 */
export function initFromWordPress() {
    const globals = window.wooAiChatbot;
    const analyticsSettings = globals?.analytics;
    if (!analyticsSettings) {
        return;
    }
    const config = {
        nonce: globals?.nonce,
        apiEndpoint: window.location.origin + '/wp-json',
    };
    // GA4
    if (analyticsSettings.ga4_enabled && analyticsSettings.ga4_measurement_id) {
        config.ga4MeasurementId = String(analyticsSettings.ga4_measurement_id);
    }
    // Meta Pixel
    if (analyticsSettings.meta_pixel_enabled && analyticsSettings.meta_pixel_id) {
        config.metaPixelId = String(analyticsSettings.meta_pixel_id);
    }
    // Mixpanel
    if (analyticsSettings.mixpanel_enabled && analyticsSettings.mixpanel_token) {
        config.mixpanelToken = String(analyticsSettings.mixpanel_token);
    }
    initAnalytics(config);
}
/**
 * Get current analytics configuration status
 */
export function getAnalyticsStatus() {
    const ga4Config = ga4Tracker.getConfig();
    const metaConfig = metaPixelTracker.getConfig();
    const mixpanelConfig = mixpanelTracker.getConfig();
    return {
        isInitialized,
        ga4: {
            available: ga4Config.isAvailable,
            initialized: ga4Config.isInitialized,
        },
        metaPixel: {
            available: metaConfig.isAvailable,
            initialized: metaConfig.isInitialized,
        },
        mixpanel: {
            available: mixpanelConfig.isAvailable,
            initialized: mixpanelConfig.isInitialized,
        },
    };
}
/**
 * Track internal event to WooAI backend
 *
 * @param eventName - Event name
 * @param params - Event parameters
 */
async function trackToBackend(eventName, params) {
    if (!analyticsConfig.apiEndpoint) {
        return;
    }
    try {
        const headers = {
            'Content-Type': 'application/json',
        };
        if (analyticsConfig.nonce) {
            headers['X-WP-Nonce'] = analyticsConfig.nonce;
        }
        await fetch(`${analyticsConfig.apiEndpoint}/woo-ai/v1/analytics/events`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
                event: eventName,
                data: params,
                timestamp: Date.now(),
            }),
        });
    }
    catch (error) {
        // Silently fail for analytics - non-critical
        if (analyticsConfig.debug) {
            console.warn('[Analytics] Failed to track to backend:', error);
        }
    }
}
/**
 * Unified event tracking function
 *
 * Sends events to all enabled analytics platforms:
 * - WooAI internal API
 * - Google Analytics 4
 * - Meta (Facebook) Pixel
 * - Mixpanel
 *
 * @param eventName - Event name
 * @param params - Optional event parameters
 */
export function trackEvent(eventName, params) {
    if (!eventName || typeof eventName !== 'string') {
        console.warn('[Analytics] Invalid event name');
        return;
    }
    // Track to internal API (fire and forget)
    trackToBackend(eventName, params).catch(() => {
        // Ignore errors
    });
    // Track to GA4
    if (ga4Tracker.isAvailable()) {
        const ga4Params = {
            event_category: params?.category ?? 'wooai_chatbot',
            event_label: params?.label,
            value: params?.value,
            currency: params?.currency,
        };
        // Copy any custom params
        if (params?.custom) {
            Object.entries(params.custom).forEach(([key, value]) => {
                if (value !== undefined) {
                    ga4Params[key] = value;
                }
            });
        }
        ga4Tracker.track(eventName, ga4Params);
    }
    // Track to Meta Pixel
    if (metaPixelTracker.isAvailable()) {
        const metaParams = {
            content_name: params?.contentName ?? params?.label,
            value: params?.value,
            currency: params?.currency,
            num_items: params?.numItems,
        };
        if (params?.contentIds) {
            metaParams.content_ids = params.contentIds.map(String);
            metaParams.content_type = 'product';
        }
        // Copy any custom params
        if (params?.custom) {
            Object.entries(params.custom).forEach(([key, value]) => {
                if (value !== undefined) {
                    metaParams[key] = value;
                }
            });
        }
        metaPixelTracker.track(eventName, metaParams);
    }
    // Track to Mixpanel
    if (mixpanelTracker.isAvailable()) {
        const mixpanelParams = {
            category: params?.category ?? 'wooai_chatbot',
            label: params?.label,
            value: params?.value,
            currency: params?.currency,
        };
        if (params?.contentIds) {
            mixpanelParams.content_ids = params.contentIds.map(String);
        }
        if (params?.contentName) {
            mixpanelParams.content_name = params.contentName;
        }
        if (params?.numItems) {
            mixpanelParams.num_items = params.numItems;
        }
        // Copy any custom params
        if (params?.custom) {
            Object.entries(params.custom).forEach(([key, value]) => {
                if (value !== undefined) {
                    mixpanelParams[key] = value;
                }
            });
        }
        mixpanelTracker.track(eventName, mixpanelParams);
    }
}
/**
 * Track ecommerce events with product data
 *
 * @param eventName - Ecommerce event name
 * @param items - Array of items
 * @param params - Additional parameters
 */
export function trackEcommerce(eventName, items, params) {
    if (!items || items.length === 0) {
        console.warn('[Analytics] No items provided for ecommerce event');
        return;
    }
    const contentIds = items.map((item) => String(item.id));
    const totalValue = params?.value ??
        items.reduce((sum, item) => sum + (item.price ?? 0) * (item.quantity ?? 1), 0);
    const numItems = items.reduce((sum, item) => sum + (item.quantity ?? 1), 0);
    const currency = params?.currency ?? 'USD';
    // Track to internal API
    trackToBackend(eventName, {
        ...params,
        contentIds,
        numItems,
        value: totalValue,
        items,
    }).catch(() => { });
    // Track to GA4 with items
    if (ga4Tracker.isAvailable()) {
        ga4Tracker.trackEcommerce(eventName === 'product_view'
            ? 'view_item'
            : eventName === 'checkout_start'
                ? 'begin_checkout'
                : eventName, items.map((item, index) => ({
            item_id: String(item.id),
            item_name: item.name,
            item_category: item.category,
            item_brand: item.brand,
            price: item.price,
            quantity: item.quantity ?? 1,
            currency,
            index,
        })), {
            value: totalValue,
            currency,
            event_category: params?.category ?? 'ecommerce',
            event_label: params?.label,
        });
    }
    // Track to Meta Pixel
    if (metaPixelTracker.isAvailable()) {
        switch (eventName) {
            case 'product_view':
                if (items.length === 1) {
                    metaPixelTracker.trackViewContent(items[0].id, items[0].name, items[0].price, currency);
                }
                else {
                    metaPixelTracker.track('products_viewed', {
                        content_ids: contentIds,
                        content_type: 'product',
                        num_items: numItems,
                    });
                }
                break;
            case 'add_to_cart':
                items.forEach((item) => {
                    metaPixelTracker.trackAddToCart(item.id, item.name, item.price, currency, item.quantity ?? 1);
                });
                break;
            case 'checkout_start':
                metaPixelTracker.trackInitiateCheckout(contentIds, totalValue, currency, numItems);
                break;
            case 'purchase':
                metaPixelTracker.trackPurchase(contentIds, totalValue, currency, items.map((item) => ({
                    id: String(item.id),
                    quantity: item.quantity ?? 1,
                    item_price: item.price,
                })));
                break;
            case 'remove_from_cart':
                metaPixelTracker.trackCustom('RemoveFromCart', {
                    content_ids: contentIds,
                    content_type: 'product',
                    value: totalValue,
                    currency,
                });
                break;
        }
    }
    // Track to Mixpanel
    if (mixpanelTracker.isAvailable()) {
        mixpanelTracker.trackEcommerce(eventName === 'product_view'
            ? 'view_item'
            : eventName === 'checkout_start'
                ? 'begin_checkout'
                : eventName, items.map((item) => ({
            id: String(item.id),
            name: item.name,
            price: item.price,
            quantity: item.quantity ?? 1,
            category: item.category,
        })), {
            value: totalValue,
            currency,
            category: params?.category ?? 'ecommerce',
            label: params?.label,
        });
    }
}
/**
 * Track chat-specific events
 *
 * @param eventName - Chat event name
 * @param params - Event parameters
 */
export function trackChatEvent(eventName, params) {
    trackEvent(eventName, {
        category: 'chat',
        ...params,
    });
}
/**
 * Set user ID across all trackers
 *
 * @param userId - User identifier
 */
export function setUserId(userId) {
    if (!userId) {
        return;
    }
    // Set in GA4
    if (ga4Tracker.isAvailable()) {
        ga4Tracker.setUserId(userId);
    }
    // Set in Mixpanel
    if (mixpanelTracker.isAvailable()) {
        mixpanelTracker.identify(String(userId));
    }
    // Note: Meta Pixel uses Advanced Matching which should be configured server-side
}
/**
 * Set user properties across all trackers
 *
 * @param properties - User properties
 */
export function setUserProperties(properties) {
    if (!properties || typeof properties !== 'object') {
        return;
    }
    // Set in GA4
    if (ga4Tracker.isAvailable()) {
        ga4Tracker.setUserProperties(properties);
    }
    // Set in Mixpanel
    if (mixpanelTracker.isAvailable()) {
        mixpanelTracker.setUserProperties(properties);
    }
}
/**
 * Reset all trackers (for logout)
 */
export function resetTrackers() {
    // Reset Mixpanel
    if (mixpanelTracker.isAvailable()) {
        mixpanelTracker.reset();
    }
}
/**
 * Enable debug mode for all trackers
 */
export function enableDebug() {
    analyticsConfig.debug = true;
    ga4Tracker.enableDebug();
    metaPixelTracker.enableDebug();
    mixpanelTracker.enableDebug();
}
/**
 * Disable debug mode for all trackers
 */
export function disableDebug() {
    analyticsConfig.debug = false;
    ga4Tracker.disableDebug();
    metaPixelTracker.disableDebug();
    mixpanelTracker.disableDebug();
}
/**
 * Default export - unified tracking interface
 */
export default {
    init: initAnalytics,
    initFromWordPress,
    track: trackEvent,
    trackEcommerce,
    trackChatEvent,
    setUserId,
    setUserProperties,
    resetTrackers,
    getStatus: getAnalyticsStatus,
    enableDebug,
    disableDebug,
    // Individual trackers
    ga4: ga4Tracker,
    metaPixel: metaPixelTracker,
    mixpanel: mixpanelTracker,
};
